import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const DEFAULT_API = `${process.env.REACT_APP_BACKEND_URL}/api/subject-understanding`;
const readSaved = (key) => { try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; } };
const errorText = (e) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || 'This topic could not be loaded. Please try again.';
};

export const useSubjectJourney = (roleId, userId, apiBase) => {
  const API = apiBase || DEFAULT_API;
  const key = `lxt_subject_position_v1:${userId}:${roleId}`;
  const saved = useRef(readSaved(key));
  const [params, setParams] = useSearchParams();
  const [route, setRoute] = useState(null);
  const [error, setError] = useState('');
  const [topic, setTopic] = useState(null);
  const [topicError, setTopicError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [storageError, setStorageError] = useState(false);
  const [expanded, setExpanded] = useState(saved.current.expanded || {});
  const [selected, setSelected] = useState(params.get('su-topic') || saved.current.selected || null);
  const [readerOpen, setReaderOpen] = useState(params.get('su-reading') === '1');
  const [view, setView] = useState(params.get('su-view') || saved.current.view || 'roadmap');
  const [stack, setStack] = useState(saved.current.stack || []);
  const mapRef = useRef(null), readerRef = useRef(null), rootRef = useRef(null);
  const requestNumber = useRef(0);
  const restore = useRef(null);
  const hasMapOrigin = useRef(saved.current.mapScroll !== undefined);

  const persist = useCallback((patch) => {
    saved.current = { ...saved.current, ...patch };
    try { localStorage.setItem(key, JSON.stringify(saved.current)); } catch { setStorageError(true); }
  }, [key]);
  const updateUrl = (id, reading, mode = view) => setParams((p) => {
    p.set('subject', 'roadmap');
    if (id) p.set('su-topic', id); else p.delete('su-topic');
    p.set('su-reading', reading ? '1' : '0'); p.set('su-view', mode);
    return p;
  }, { replace: true });
  const readerContext = () => ({
    readerScroll: readerRef.current?.scrollTop || 0,
    openDetails: Array.from(readerRef.current?.querySelectorAll('details[open]') || []).map((el) => el.dataset.testid),
  });
  const mapPosition = () => mapRef.current?.clientHeight ? mapRef.current.scrollTop : saved.current.mapScroll || 0;
  const savePositions = () => persist({ mapScroll: mapPosition(),
    positions: { ...(saved.current.positions || {}), ...(selected ? { [selected]: readerContext() } : {}) } });

  const fetchTopic = useCallback(async (id, context = null) => {
    const req = ++requestNumber.current;
    setLoading(true); setTopic(null); setTopicError('');
    try {
      const { data } = await axios.get(`${API}/${encodeURIComponent(roleId)}/topics/${encodeURIComponent(id)}`);
      if (req !== requestNumber.current) return;
      const previousHash = saved.current.hashes?.[data.node.lesson_id || id];
      if (previousHash && previousHash !== data.content_hash) setNotice('This content has been revised. Your previous attempts and preferences have not changed.');
      persist({ hashes: { ...saved.current.hashes, [data.node.lesson_id || id]: data.content_hash },
        parents: { ...saved.current.parents, [id]: data.parent_id } });
      restore.current = context || saved.current.positions?.[id] || { readerScroll: 0 };
      setTopic(data);
    } catch (e) {
      if (req !== requestNumber.current) return;
      setTopicError(errorText(e));
      if ([401, 403, 423].includes(e?.response?.status)) { setRoute(null); setError(errorText(e)); }
    } finally { if (req === requestNumber.current) setLoading(false); }
  }, [roleId, persist, API]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setRoute(null); setTopic(null); setError('');
      try {
        const { data } = await axios.get(`${API}/${encodeURIComponent(roleId)}`);
        if (!active) return;
        setRoute(data);
        let id = new URLSearchParams(window.location.search).get('su-topic') || saved.current.selected;
        if (id && !data.nodes.some((n) => n.id === id)) {
          const parent = saved.current.parents?.[id];
          id = data.nodes.some((n) => n.id === parent) ? parent : data.entry_node_id;
          setNotice('Your saved topic has changed or is no longer available. We opened its surviving lesson, or the start of this route. Your previous attempts are unchanged.');
        }
        setSelected(id || null);
        if (id) {
          const nextExpanded = { ...saved.current.expanded };
          let parent = data.nodes.find((n) => n.id === id)?.parent_id;
          while (parent) {
            nextExpanded[parent] = true;
            parent = data.nodes.find((n) => n.id === parent)?.parent_id;
          }
          setExpanded(nextExpanded);
          persist({ selected: id, expanded: nextExpanded }); fetchTopic(id);
        }
      } catch (e) { if (active) setError(errorText(e)); }
    };
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { active = false; requestNumber.current += 1; document.removeEventListener('visibilitychange', onVisible); };
  }, [roleId, userId, fetchTopic, persist]);

  useEffect(() => {
    if (!route || !mapRef.current) return;
    mapRef.current.scrollTop = saved.current.mapScroll || 0;
    if (!hasMapOrigin.current && mapRef.current.clientHeight && selected) {
      requestAnimationFrame(() => rootRef.current?.querySelector(`[data-testid="su-topic-${CSS.escape(selected)}"]`)?.scrollIntoView({ block: 'center', behavior: 'instant' }));
    }
  }, [route]); // selection changes preserve the originating map position
  useEffect(() => {
    if (!topic || !readerRef.current) return;
    const context = restore.current;
    requestAnimationFrame(() => {
      if (!readerRef.current) return;
      if (context?.openDetails) readerRef.current.querySelectorAll('details').forEach((el) => { el.open = context.openDetails.includes(el.dataset.testid); });
      readerRef.current.scrollTop = context?.readerScroll || 0;
      document.getElementById('su-reader-title')?.focus({ preventScroll: true });
    });
  }, [topic]);

  const select = (id, core = false, context = null) => {
    if (mapRef.current?.clientHeight) hasMapOrigin.current = true;
    savePositions();
    if (core && selected) {
      const next = [...stack, { id: selected, parent: topic?.parent_id, ...readerContext(), mapScroll: mapPosition(), expanded, view }];
      setStack(next); persist({ stack: next });
    }
    const node = route.nodes.find((n) => n.id === id);
    if (node?.parent_id) { const next = { ...expanded, [node.parent_id]: true }; setExpanded(next); persist({ expanded: next }); }
    setSelected(id); setReaderOpen(true); setNotice('');
    persist({ selected: id }); updateUrl(id, true); fetchTopic(id, context || { readerScroll: 0 });
  };
  const back = () => {
    savePositions(); setReaderOpen(false); setView('roadmap'); persist({ view: 'roadmap' }); updateUrl(selected, false, 'roadmap');
    requestAnimationFrame(() => {
      if (mapRef.current) mapRef.current.scrollTop = saved.current.mapScroll || 0;
      const target = rootRef.current?.querySelector(`[data-testid="su-topic-${CSS.escape(selected || '')}"]`);
      if (!hasMapOrigin.current) { target?.scrollIntoView({ block: 'center', behavior: 'instant' }); hasMapOrigin.current = true; }
      target?.focus({ preventScroll: true });
    });
  };
  const returnToTopic = () => {
    const previous = stack[stack.length - 1]; if (!previous) return;
    const next = stack.slice(0, -1); setStack(next); persist({ stack: next });
    let id = previous.id;
    const revised = !route.nodes.some((n) => n.id === id);
    if (revised) id = route.nodes.some((n) => n.id === previous.parent) ? previous.parent : route.entry_node_id;
    select(id, false, previous);
    setExpanded(previous.expanded); setView(previous.view);
    updateUrl(id, true, previous.view);
    persist({ expanded: previous.expanded, mapScroll: previous.mapScroll, view: previous.view });
    if (mapRef.current) mapRef.current.scrollTop = previous.mapScroll;
    if (revised) setNotice('This topic was revised or removed. We opened its surviving lesson. Your previous attempts are unchanged.');
  };
  const toggle = (id) => { const next = { ...expanded, [id]: !expanded[id] }; setExpanded(next); persist({ expanded: next }); };
  const changeView = (next) => {
    savePositions(); setView(next); persist({ view: next });
    if (next === 'roadmap') { back(); return; }
    const id = selected || route.entry_node_id;
    setReaderOpen(true); setSelected(id); persist({ selected: id }); updateUrl(id, true, next);
    fetchTopic(id, saved.current.positions?.[id]);
  };
  return { route, error, topic, topicError, loading, notice, storageError, expanded, selected, readerOpen, view, stack,
    mapRef, readerRef, rootRef, persist, savePositions, select, back, returnToTopic, toggle, changeView, fetchTopic };
};